import type { AppEmbed } from '@thoughtspot/visual-embed-sdk';

export interface ClusterBanner {
  banner_text: string;
  under_maintenance: boolean;
}

/**
 * JSON payload carried by the `dashboard-scheduled-maintenance-banner` Unleash
 * variant. Set by our team ahead of a known ThoughtSpot outage window (advance
 * warning). The banner shows while the flag is enabled and the current time is
 * within the window, then auto-hides once `endsAt` passes.
 */
export interface ScheduledMaintenancePayload {
  /** Customer-facing, unbranded message to display in the banner. */
  message: string;
  /** ISO 8601 timestamp; the banner stops showing once this time passes. */
  endsAt: string;
  /**
   * Optional ISO 8601 timestamp. When set, the banner stays hidden until this
   * time, letting us pre-stage the flag (e.g. enable it now, appear 48h out).
   * Omit to show as soon as the flag is enabled.
   */
  startsAt?: string;
}

/**
 * @description Utility function to report errors to Sentry with context
 */
export type ThoughtSpotEmbedAction =
  | 'navigation'
  | 'cleanup-destroy'
  | 'cleanup-remove-frame'
  | 'cleanup'
  | 'initializeEmbed'
  | 'walkmeInjection'
  | 'walkmeInjectionOnLoad'
  | 'setupWalkMe';

/**
 * @description Represents the underlying iframe element used by ThoughtSpot embeds
 */
export interface ThoughtSpotUnderlyingFrame {
  contentWindow?: {
    document?: { readyState?: string };
    addEventListener?: (type: 'load', listener: () => void, options?: { once?: boolean }) => void;
  };
  parentNode?: (Node & { removeChild?: (n: Node) => Node }) | null;
  isConnected?: boolean;
}

/**
 * @description Context type for managing the state of a pre-rendered ThoughtSpot App Embed
 */
export interface ThoughtSpotCoords {
  left: number;
  top: number;
  height: number;
  width: number;
}

/**
 * Payload from a LIVEBOARD-target custom action.
 * `payload.data` shape when CustomActionTarget.LIVEBOARD fires.
 */
export interface LiveboardSharePayload {
  id: string;
  liveboardId: string;
  pinboardDetails: {
    id: string;
    name?: string;
    description?: string;
    authorDisplayName?: string;
    authorGuid?: string;
    containers?: Array<{
      id: string;
      name?: string;
      answerId?: string;
      [key: string]: unknown;
    }>;
    [key: string]: unknown;
  };
}

/**
 * Payload from an ANSWER or VIZ-target custom action.
 * `payload.data` shape when CustomActionTarget.ANSWER or CustomActionTarget.VIZ fires.
 */
export interface AnswerSharePayload {
  id: string;
  embedAnswerData: {
    id?: string;
    name?: string;
    columns?: unknown[];
    data?: unknown[];
    reportBookMetadata?: {
      headerMetadata?: {
        id?: string;
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  vizId?: string;
  contextMenuPoints?: {
    clickedPoint: unknown;
    selectedPoints: unknown[];
  };
}

/** Discriminated union — narrows based on which key is present */
export type ShareDialogPayload = LiveboardSharePayload | AnswerSharePayload;

/** Type guard: payload came from a Liveboard-level action */
export function isLiveboardSharePayload(p: ShareDialogPayload): p is LiveboardSharePayload {
  return 'pinboardDetails' in p;
}

/** Type guard: payload came from an Answer or Viz-level action */
export function isAnswerSharePayload(p: ShareDialogPayload): p is AnswerSharePayload {
  return 'embedAnswerData' in p;
}

/**
 * @description Context type for managing the state of a pre-rendered ThoughtSpot App Embed
 */
export interface PreRenderedAppEmbedContextType {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  path?: string;
  setPath: (path?: string) => void;
  coords: ThoughtSpotCoords;
  setCoords: (coords: ThoughtSpotCoords) => void;
  embedRef: React.RefObject<AppEmbed | null>;
  isAuthenticated: boolean;
  handleNavigation: (path: string) => void;
  isReady: boolean;
  setIsReady: (ready: boolean) => void;
  initializeView: (path: string, coords: ThoughtSpotCoords) => void;
  // Share dialog
  isShareDialogOpen: boolean;
  sharePayload: ShareDialogPayload | null;
  openShareDialog: (payload: ShareDialogPayload) => void;
  closeShareDialog: () => void;
}
